import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import { resolveAssetUrl } from '../services/api';
import { Lock, TrendingUp, TrendingDown, ShoppingCart, MinusCircle } from 'lucide-react';

const ROLE_MAP = {
  batsman:         ['role-bat',  'BAT'],
  bowler:          ['role-bowl', 'BOWL'],
  'all-rounder':   ['role-all',  'ALL'],
  'wicket-keeper': ['role-wk',   'WK'],
};

function initials(name) {
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

function PlayerPhoto({ player, size = 52 }) {
  const [imgError, setImgError] = useState(false);
  const tc = player.team_color || '#3b82f6';
  const hasPhoto = player.photo_url && player.photo_url !== 'none' && !imgError;
  if (hasPhoto) {
    return (
        <div style={{ width:size, height:size, borderRadius:'50%', overflow:'hidden', border:`2px solid ${tc}55`, flexShrink:0, background:tc+'22' }}>
        <img src={resolveAssetUrl(player.photo_url)} alt={player.name} onError={() => setImgError(true)} style={{ width:'100%', height:'100%', objectFit:'cover', objectPosition:'top center' }} />
      </div>
    );
  }
  return (
    <div style={{ width:size, height:size, borderRadius:'50%', flexShrink:0, background:tc+'22', color:tc, border:`2px solid ${tc}44`, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--font-display)', fontWeight:700, fontSize:size>48?'1rem':'0.8rem' }}>
      {initials(player.name)}
    </div>
  );
}

export default function PlayerCard({ player, holding, bucketOrder, marketStatus, onBucketChange, onToast }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const marketLocked  = marketStatus?.isLive || !marketStatus?.tradingWindowOpen;
  const playerLiveNow = !!marketStatus?.isLive && !!player.is_playing_today;
  const changePct     = parseFloat(player.price_change_pct || 0);
  const changeClass   = changePct > 0 ? 'up' : changePct < 0 ? 'down' : 'flat';
  const changeLabel   = `${changePct > 0 ? '+' : ''}${changePct.toFixed(2)}%`;
  const [cls,,roleLabel] = [ROLE_MAP[player.role]?.[0]||'role-bat', null, ROLE_MAP[player.role]?.[1] || player.role?.slice(0,3).toUpperCase()];
  const teamColor     = player.team_color || '#3b82f6';
  const inBucket      = !!bucketOrder;
  const bucketType    = bucketOrder?.type;

  const addToBucket = async (type, e) => {
    e.stopPropagation();
    if (marketLocked) { onToast?.('Trading is locked', 'error'); return; }
    setLoading(true);
    try {
      await API.post('/bucket/add', { player_id: player.id, type });
      onToast?.(`${player.name} added to bucket`, 'success');
      onBucketChange?.();
    } catch (err) {
      onToast?.(err.response?.data?.error || 'Failed to add', 'error');
    } finally { setLoading(false); }
  };

  const removeFromBucket = async (e) => {
    e.stopPropagation();
    if (!bucketOrder?.id) return;
    setLoading(true);
    try {
      await API.delete(`/bucket/${bucketOrder.id}`);
      onToast?.('Removed from bucket', 'info');
      onBucketChange?.();
    } catch (err) {
      onToast?.(err.response?.data?.error || 'Failed to remove', 'error');
    } finally { setLoading(false); }
  };

  return (
    <div className="player-card" onClick={() => navigate(`player/${player.id}`)} style={{ opacity: marketLocked ? 0.88 : 1, position:'relative', overflow:'hidden' }}>
      <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:teamColor, opacity:0.7 }} />
      <div style={{ display:'flex', alignItems:'center', gap:10, paddingTop:4 }}>
        <PlayerPhoto player={player} size={50} />
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:5, flexWrap:'wrap' }}>
            <span style={{ fontWeight:600, fontSize:'0.88rem', color:'var(--text-primary)', lineHeight:1.2 }}>{player.name}</span>
            {playerLiveNow && <span className="live-badge"><span className="live-dot" /> LIVE</span>}
            {inBucket && (
              <span style={{ fontSize:'0.62rem', fontWeight:700, padding:'1px 6px', borderRadius:20, background: bucketType==='buy'?'rgba(16,185,129,0.15)':'rgba(239,68,68,0.15)', color: bucketType==='buy'?'var(--green)':'var(--red)', border: `1px solid ${bucketType==='buy'?'rgba(16,185,129,0.3)':'rgba(239,68,68,0.3)'}` }}>
                IN BUCKET
              </span>
            )}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:3 }}>
            <span style={{ fontSize:'0.7rem', fontWeight:700, color:teamColor }}>{player.team_short}</span>
            <span className={`role-badge ${ROLE_MAP[player.role]?.[0]||'role-bat'}`}>{roleLabel}</span>
          </div>
        </div>
      </div>

      {playerLiveNow && (
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:6 }}>
          {player.live_runs > 0 && <span className="stat-chip">🏏 <strong>{player.live_runs}</strong>{player.live_balls_faced>0&&<span style={{color:'var(--text-muted)',fontSize:'0.68rem'}}>({player.live_balls_faced}b)</span>}</span>}
          {player.live_wickets > 0 && <span className="stat-chip">🎯 <strong>{player.live_wickets}W</strong></span>}
          {player.live_catches > 0 && <span className="stat-chip">🤲 <strong>{player.live_catches}c</strong></span>}
        </div>
      )}

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginTop:10 }}>
        <div>
          <div style={{ fontSize:'0.65rem', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:2 }}>Stock Price</div>
          <div style={{ fontFamily:'var(--font-display)', fontSize:'1.25rem', fontWeight:700, color:'var(--text-primary)', lineHeight:1 }}>
            {parseFloat(player.current_stock_price).toFixed(1)}<span style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginLeft:2 }}>pts</span>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:4 }}>
          {changePct !== 0 && (changePct > 0 ? <TrendingUp size={12} color="var(--green)" /> : <TrendingDown size={12} color="var(--red)" />)}
          <span className={`price-change ${changeClass}`}>{changeLabel}</span>
        </div>
      </div>

      <div style={{ display:'flex', gap:6, marginTop:10 }} onClick={e => e.stopPropagation()}>
        {inBucket ? (
          <button
            onClick={removeFromBucket}
            disabled={loading}
            style={{ width:'100%', padding:'8px', borderRadius:8, border:`1px solid ${bucketType==='buy'?'rgba(16,185,129,0.3)':'rgba(239,68,68,0.3)'}`, background: bucketType==='buy'?'rgba(16,185,129,0.08)':'rgba(239,68,68,0.08)', color: bucketType==='buy'?'var(--green)':'var(--red)', cursor:'pointer', fontSize:'0.8rem', fontWeight:600, display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}
          >
            <MinusCircle size={12} /> In Bucket — Remove
          </button>
        ) : holding ? (
          <>
            <div style={{ flex:1, fontSize:'0.72rem', color:'var(--text-secondary)', display:'flex', alignItems:'center', gap:4 }}>
              <span style={{ color: parseFloat(player.current_stock_price) >= parseFloat(holding.buy_price) ? 'var(--green)' : 'var(--red)', fontWeight:600 }}>
                {parseFloat(player.current_stock_price) >= parseFloat(holding.buy_price) ? '↑' : '↓'}
              </span>
              @ {parseFloat(holding.buy_price).toFixed(1)}
            </div>
            <button
              onClick={(e) => addToBucket('sell', e)}
              disabled={loading || marketLocked}
              style={{ flex:1, padding:'7px 10px', borderRadius:8, border:'none', background: marketLocked?'var(--bg-elevated)':'var(--red)', color: marketLocked?'var(--text-muted)':'#fff', cursor: marketLocked?'not-allowed':'pointer', fontSize:'0.8rem', fontWeight:600, display:'flex', alignItems:'center', justifyContent:'center', gap:4, fontFamily:'var(--font-display)' }}
            >
              {marketLocked ? <><Lock size={11}/> Locked</> : <><ShoppingCart size={11}/> + Sell</>}
            </button>
          </>
        ) : (
          <button
            onClick={(e) => addToBucket('buy', e)}
            disabled={loading || marketLocked}
            style={{ width:'100%', padding:'8px', borderRadius:8, border:'none', background: marketLocked?'var(--bg-elevated)':'var(--green)', color: marketLocked?'var(--text-muted)':'#fff', cursor: marketLocked?'not-allowed':'pointer', fontSize:'0.82rem', fontWeight:600, display:'flex', alignItems:'center', justifyContent:'center', gap:5, fontFamily:'var(--font-display)' }}
          >
            {marketLocked ? <><Lock size={11}/> Locked</> : <><ShoppingCart size={11}/> + Bucket · {parseFloat(player.current_stock_price).toFixed(1)}</>}
          </button>
        )}
      </div>
    </div>
  );
}

export { PlayerPhoto };
